import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Button, Badge } from "antd";
import { SaveOutlined, PlusOutlined } from "@ant-design/icons";

// API Imports
import { createSample, fetchSample, reAnalyseSample, updateSample } from "../../../../api/labiq/SampleApi";
import { Sample, SampleOA, defaultSample } from "../../../../types/lab/SampleTypes";
import { fetchCompanies } from "../../../../api/admin/companyApi";

// Shared components
import { DetailDrawer } from "../../../../components/shared";

// Import Modal Components
import AnalysisModal from "./sampleModalComponents/AnalysisModal";
import GeneCodesTab from "../../../healthcare/results/testOrderComponents/GeneCodesTab";

//Types
import { Company } from "../../../../types/admin/CompanyTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";

// Import the utilities
import { useSampleUpdater, validateSampleFields } from "../../../../utils/lab/sampleUpdateUtil";
import { useNotifications } from "../../../../context/NotificationContext";

// Tab Components
import SampleOverviewTab from "./sampleModalComponents/SampleOverviewTab";
import SampleDetailsTab from "./sampleModalComponents/SampleDetailsTab";
import LabAnalysisTab from "./sampleModalComponents/LabAnalysisTab";
import ProtocolsTab from "./sampleModalComponents/ProtocolsTab";
import GeneticDataTab from "./sampleModalComponents/GeneticDataTab";
import GenotyperSection from "./sampleModalComponents/GenotyperSection";
import AnalysisResultsTab from "./sampleModalComponents/AnalysisResultsTab";

import GeneticSampleAnalysisModal from "../../../gene.codes/GeneticWheel/WheelModal";

interface SampleModalProps {
	isOpen: boolean;
	onClose: () => void;
	sample: Sample;
	patient: Patient | null;
	onFormSubmission: () => void;
}

interface FieldError {
	field: string;
	message: string;
}

const SampleModal: React.FC<SampleModalProps> = ({ isOpen, onClose, sample, patient, onFormSubmission }) => {
	const [sampleData, setSampleData] = useState<Sample>(defaultSample);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

	const [isDisabled, setIsDisabled] = useState<boolean>(false);
	const [companies, setCompanies] = useState<Company[]>([]);
	const [analysisModalOpen, setAnalysisModalOpen] = useState<boolean>(false);
	const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
	const [activeTab, setActiveTab] = useState<string>("Overview");
	const [geneCodesModalOpen, setGeneCodesModalOpen] = useState<boolean>(false);

	const userCompany = getSessionUser().company;
	const latestSampleDataRef = useRef<Sample>(defaultSample);

	const { showError, showInfo, showSuccess, showWarning } = useNotifications();

	useEffect(() => {
		latestSampleDataRef.current = sampleData;
	}, [sampleData]);

	const saveSampleData = useCallback(async () => {
		if (sample.id === 0 || isDisabled) return;

		try {
			await updateSample(sample.id, latestSampleDataRef.current);
			showSuccess("Sample auto-saved successfully");
		} catch (error) {
			handleErrors(error);
		}
	}, [sample.id, isDisabled]);

	const { updateField, updateMultipleFields, triggerSave, cancelPendingSaves } = useSampleUpdater(setSampleData, saveSampleData, setSaveStatus, 2000);

	useEffect(() => {
		if (isOpen) {
			const initializeData = async () => {
				let clientData = userCompany || sample.client;

				if (userCompany === "Mygene" || userCompany === "DNAIQ") {
					try {
						const response = await fetchCompanies(1, 100, "");
						setCompanies(response.data);
					} catch (error) {
						handleErrors(error);
					}
				} 


				//fetch the full sample data including relations if editing an existing sample
				let smpl = await fetchSample(sample.sampleID);

				setSampleData({
					...smpl,
					client: clientData,
					sampleOA: {
						...(smpl.sampleOA as SampleOA),
						client: clientData,
					},
				});

				if (userCompany !== "DNAIQ" && userCompany !== "Mygene") {
					if (sample.sampleOA?.status === undefined || sample.sampleOA?.status === null) {
						setIsDisabled(false);
						return;
					}
					if (sample.sampleOA?.status !== "dispatched" && sample.sampleOA?.status !== "returning") {
						setIsDisabled(true);
					}
				}
			};

			initializeData();
		}

		return () => {
			cancelPendingSaves();
		};
	}, [isOpen]);

	const validateForm = (): boolean => {
		const validationErrors = validateSampleFields(sampleData);

		if (Object.keys(validationErrors).length === 0) {
			setFieldErrors([]);
			return true;
		}

		const newFieldErrors = Object.entries(validationErrors).map(([field, message]) => ({
			field,
			message,
		}));

		setFieldErrors(newFieldErrors);
		setErrorMessages(Object.values(validationErrors));

		return false;
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);

		if (!validateForm()) {
			setIsSubmitting(false);
			return;
		}

		try {
			if (sample.id !== 0) {
				triggerSave();
				showSuccess("Sample updated successfully");
			} else {
				await createSample(sampleData);
				showSuccess("Sample created successfully");
			}

			onFormSubmission();
			onClose();
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response) {
			if (error.response.data?.errors) {
				const messages = Object.entries(error.response.data.errors).flatMap(([field, errs]) => {
					const errors = errs as string[];

					setFieldErrors((prevErrors) => [...prevErrors, ...errors.map((message) => ({ field, message }))]);

					return errors;
				});

				setErrorMessages(messages);
			} else if (error.response.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message || "An unknown error occurred."]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const openAnalysisModal = () => {
		setAnalysisModalOpen(true);
	};

	const closeAnalysisModal = (updatedSample?: Sample) => {
		if (updatedSample && updatedSample.sampleOA) {
			updateField("sampleOA.analysis_requested", updatedSample.sampleOA.analysis_requested);
		}
		setAnalysisModalOpen(false);
	};

	const getFieldError = (fieldName: string): string | undefined => {
		const error = fieldErrors.find((err) => err.field === fieldName);
		return error?.message;
	};

	const getSaveStatus = () => {
		switch (saveStatus) {
			case "saving":
				return <Badge status="processing" text={<span style={{ fontSize: 12 }}>Saving…</span>} />;
			case "success":
				return <Badge status="success" text={<span style={{ fontSize: 12 }}>Saved</span>} />;
			case "error":
				return <Badge status="error" text={<span style={{ fontSize: 12 }}>Error</span>} />;
			default:
				return null;
		}
	};

	const reAnalyseData = async () => {
		if (sample.id === 0) return;

		setIsSubmitting(true);
		try {
			const response = await reAnalyseSample(sample.sampleID);
			showSuccess("Re-analysis request run successfully");
			setErrorMessages([]);
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const openGeneCodesModal = () => {
		setGeneCodesModalOpen(true);
	};

	// Tab items configuration
	const tabItems = [
		{
			key: "Overview",
			label: "Overview",
			children: <SampleOverviewTab sample={sampleData} patient={patient} />,
		},
		{
			key: "Details",
			label: "Sample Details",
			children: (
				<SampleDetailsTab
					sampleData={sampleData}
					updateSampleData={updateField}
					updateMultipleFields={updateMultipleFields}
					setErrorMessages={setErrorMessages}
					getFieldError={getFieldError}
					companies={companies}
					disabled={isDisabled}
				/>
			),
		},
		{
			key: "LabAnalysis",
			label: "Lab Analysis",
			children: (
				<LabAnalysisTab
					sampleData={sampleData}
					updateSampleData={updateField}
					updateMultipleFields={updateMultipleFields}
					getFieldError={getFieldError}
					disabled={isDisabled}
				/>
			),
		},
		{
			key: "Extraction",
			label: "Extraction",
			children: <ProtocolsTab sample={sampleData} />,
		},
		{
			key: "Analysis Protocols",
			label: "Analysis Protocols",
			children: <AnalysisResultsTab analyses={sampleData.analyses || []} />,
		},
		{
			key: "GeneticData",
			label: "Genetic Data",
			children: <GeneticDataTab qsBases={sampleData.qsBases || []} />,
		},
		{
			key: "Genotyper",
			label: "Genotyper",
			children: <GenotyperSection sampleId={sampleData.sampleID} />,
		},
		// {
		// 	key: "History",
		// 	label: "History",
		// 	children: <HistoryTab history={history} />,
		// },
		...(patient
			? [
				{
					key: "Gene.Codes",
					label: "Gene.Codes",
					children: <GeneCodesTab clientSampleId={sample.sampleOA?.client_sampleID as string} patient={patient as Patient} />,
				},
			]
			: []),
	];

	// ── Header actions ─────────────────────────────────────────
	const headerActions = (
		<>
			{sampleData.qsBases && sampleData.qsBases.length > 0 && (
				<Button type="default" size="small" onClick={openGeneCodesModal} disabled={isSubmitting}>
					Gene.Codes
				</Button>
			)}
			{(userCompany === "DNAIQ" || userCompany === "Mygene") && sampleData.qsBases && sampleData.qsBases.length > 0 && (
				<Button type="default" size="small" onClick={reAnalyseData} disabled={isSubmitting}>
					Re-import Data
				</Button>
			)}
			<Button type="default" size="small" icon={<PlusOutlined />} onClick={openAnalysisModal} disabled={isSubmitting}>
				Add Analysis
			</Button>
			<Button type="primary" size="small" icon={<SaveOutlined />} onClick={handleSubmit} loading={isSubmitting}>
				Save Changes
			</Button>
		</>
	);

	return (
		<>
			<DetailDrawer
				open={isOpen}
				onClose={() => { onFormSubmission(); onClose(); }}
				width="85vw"
				title={sample.id ? `Sample ${sample.sampleID}` : "Create New Sample"}
				subtitle={`ID: ${sample.id || "New"}`}
				titleMeta={getSaveStatus()}
				headerActions={headerActions}
				tabs={tabItems}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				errorMessages={errorMessages}
				onClearErrors={() => setErrorMessages([])}
			/>

			{analysisModalOpen && <AnalysisModal isOpen={analysisModalOpen} onClose={closeAnalysisModal} sample={sampleData} />}

			{geneCodesModalOpen && <GeneticSampleAnalysisModal isOpen={geneCodesModalOpen} onClose={() => setGeneCodesModalOpen(false)} patient={patient as Patient} sample={sampleData} surface="lab-sample" />}
		</>
	);
};

export default SampleModal;
